/**
 * Basecamp Extension Chrome - UI Prototype v2
 * Full screen inventory + vision mocks for Listening / Activity.
 *
 * Access: http://flownotes.localhost/?test=basecamp-extension
 *
 * Bets:
 * - docs/product/bets/2026-07-11-basecamp-extension-chrome-standard-surfaces.md
 * - docs/product/bets/2026-06-04-basecamp-extension-triggers-v1.md (Listening + Activity production)
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Bookmark,
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Eye,
  FileText,
  FolderOpen,
  ListTodo,
  MessageSquare,
  MoreHorizontal,
  PanelRight,
  Radio,
  RefreshCw,
  Search,
  Square,
  Tent,
  User,
  X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { MinimizedSection } from '@/components/layout/minimized-section'
import { EntityContextMenu } from '@/components/features/entity-context-menu'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { ProjectsFocusPanel } from '@/components/test-basecamp-extension-projects-focus'

type Scene =
  | 'settings-disconnected'
  | 'settings-connected'
  | 'settings-listening'
  | 'browse'
  | 'bookmarks'
  | 'todos'
  | 'todos-preview'
  | 'projects'
  | 'projects-focus'
  | 'project'
  | 'project-todolist'
  | 'activity'
  | 'todo-unlinked'
  | 'todo-empty-notes'
  | 'create-spec'
  | 'todo-linked'

interface BcComment {
  id: string
  author: string
  at: string
  text: string
  role?: string
}

interface BcTodo {
  id: string
  name: string
  completed: boolean
  dueOn: string | null
  daysOverdue?: number
  assignee: string | null
  list: string
  project: string
  isHop: boolean
  notes: string
  comments: BcComment[]
  linkedDoc?: { title: string; path: string } | null
  bookmarked?: boolean
  workSpec?: 'in-sync' | 'stale' | 'none'
  activity?: string | null
}

interface ActivityEvent {
  id: string
  /** Day bucket for grouping, newest first within day. */
  dayKey: string
  dayLabel: string
  /** Clock time within the day, e.g. "3:00pm". */
  time: string
  kind: 'comment' | 'completed' | 'updated' | 'created'
  actor: string
  /** Actor is the signed-in user (Rowan in mock). */
  isMe: boolean
  /** Hop teammate (not a client contact). */
  isTeam: boolean
  title: string
  todoId: string
  project: string
  projectId: string
  linked: boolean
  flowStatus?: string | null
  /** Comment body or todo metadata snippet. */
  excerpt?: string | null
}

const SCENES: { id: Scene; label: string }[] = [
  { id: 'settings-disconnected', label: 'Settings (off)' },
  { id: 'settings-connected', label: 'Settings (on)' },
  { id: 'settings-listening', label: 'Listening' },
  { id: 'browse', label: 'Browse empty' },
  { id: 'bookmarks', label: 'Bookmarks' },
  { id: 'todos', label: 'Todos' },
  { id: 'todos-preview', label: 'Todos+preview' },
  { id: 'projects', label: 'Projects' },
  { id: 'projects-focus', label: 'Projects focus' },
  { id: 'project', label: 'Project' },
  { id: 'project-todolist', label: 'Todolist' },
  { id: 'activity', label: 'Activity' },
  { id: 'todo-unlinked', label: 'Todo' },
  { id: 'todo-empty-notes', label: 'Empty notes' },
  { id: 'create-spec', label: 'Create' },
  { id: 'todo-linked', label: 'Linked' },
]

const PAD = [8, 20, 32, 44, 56] as const

const MOCK_TODOS: BcTodo[] = [
  {
    id: '9821550339',
    name: 'Fix Happiness Calendar settings',
    completed: false,
    dueOn: '2026-07-15',
    assignee: 'Rowan',
    list: 'GGSC - Web',
    project: 'Greater Good Science Center',
    isHop: false,
    notes:
      'Calendar widget ignores per-event timezone. Confirm with Lauren before changing default.',
    comments: [
      {
        id: 'c1',
        author: 'Lauren',
        at: 'Jul 9',
        text: 'Any update on timing? We need this before the next happiness drop.',
      },
      {
        id: 'c2',
        author: 'Rowan',
        at: 'Jul 10',
        text: 'Repro locally. Will push a fix behind the existing settings form.',
      },
    ],
    linkedDoc: null,
    bookmarked: true,
    workSpec: 'none',
    activity: 'Jul 10: Rowan: Will push a fix...',
  },
  {
    id: '9669131017',
    name: 'Restructure events page - three sections',
    completed: false,
    dueOn: '2026-07-18',
    assignee: 'Rowan',
    list: 'GGSC - Web',
    project: 'Greater Good Science Center',
    isHop: false,
    notes:
      'LAUREN SAID:\n\nWe need a monthly livestream series section on Events. Right now Past / Upcoming / Featured are not clear enough for editors, and Featured sometimes feels auto-driven by date instead of editorial judgment.\n\nPlease restructure the Events page into three labeled sections:\n\n- Past - chronological archive of completed events\n- Upcoming - scheduled / published future events\n- Featured - editor-picked highlights (not auto from date)\n\nKeep the existing EE channel structure where possible. If a section needs more breathing room, use expandable boxes rather than dumping everything above the fold.\n\nTiming: aim for a reviewable HTML mock this week - Claude is fine for a first pass on the layout copy, but Lauren wants to approve Featured wording before launch.\n\nAlso attach the HTML mock (ggsc-events-redesign.html) for review.',
    comments: [
      {
        id: 'c3',
        author: 'Dan',
        at: 'Apr 23',
        text: 'Featured should stay editorial - not auto from date.',
      },
      {
        id: 'c4',
        author: 'Lauren',
        at: 'Apr 22',
        text: 'Can we keep the monthly livestream series as its own section?',
        role: 'Client',
      },
      {
        id: 'c5',
        author: 'Rowan',
        at: 'Apr 22',
        text: 'Yes - Past / Upcoming / Featured. Will revise the HTML mock.',
      },
    ],
    linkedDoc: null,
    bookmarked: true,
    workSpec: 'stale',
    activity: 'Apr 23: Dan: Featured should stay...',
  },
  {
    id: '8033530121',
    name: 'Develop proposal to improve Quiz publishing system',
    completed: false,
    dueOn: '2026-03-19',
    daysOverdue: 115,
    assignee: 'Rowan',
    list: 'General',
    project: 'GGIA',
    isHop: false,
    notes: '',
    comments: [],
    linkedDoc: {
      title: 'Develop proposal to improve Quiz publishing system',
      path: 'hop-studios/clients/greater-good/ggia/todos/8033530121-quiz-publishing.md',
    },
    bookmarked: true,
    workSpec: 'in-sync',
    activity: null,
  },
  {
    id: '9900112233',
    name: 'Weekly product sync notes',
    completed: false,
    dueOn: '2026-07-12',
    assignee: 'Rowan',
    list: 'Product',
    project: 'Hop Studios Internal',
    isHop: true,
    notes: 'Agenda for Monday sync.',
    comments: [],
    linkedDoc: null,
    workSpec: 'none',
    activity: null,
  },
  {
    id: '9711001001',
    name: 'Update homepage hero copy',
    completed: true,
    dueOn: '2026-06-01',
    assignee: 'Alice',
    list: 'GGSC - Web',
    project: 'Greater Good Science Center',
    isHop: false,
    notes: 'Shipped.',
    comments: [],
    linkedDoc: null,
    workSpec: 'none',
    activity: null,
  },
]

/** Todo lists per project (project home). Empty lists allowed. */
const MOCK_LISTS: Record<string, { id: string; name: string }[]> = {
  '1': [
    { id: 'l-ggsc-web', name: 'GGSC - Web' },
    { id: 'l-ggsc-mkt', name: 'Marketing' },
  ],
  '2': [
    { id: 'l-ggia-gen', name: 'General' },
    { id: 'l-ggia-priv', name: 'Private' },
  ],
  '3': [{ id: 'l-exida', name: 'General' }],
  '4': [{ id: 'l-hop', name: 'Product' }],
}

const MOCK_PROJECTS = [
  {
    id: '1',
    name: 'Greater Good Science Center',
    listening: true,
    lastEvent: '2h ago',
    scope: 'client' as const,
    gitPath: 'hopwork/clients/ggsc',
    status: 'active' as const,
  },
  {
    id: '2',
    name: 'GGIA',
    listening: true,
    lastEvent: 'Yesterday',
    scope: 'client' as const,
    gitPath: null as string | null,
    status: 'active' as const,
  },
  {
    id: '3',
    name: 'Exida',
    listening: false,
    lastEvent: null,
    scope: 'client' as const,
    gitPath: null as string | null,
    status: 'active' as const,
  },
  {
    id: '4',
    name: 'Hop Studios Internal',
    listening: true,
    lastEvent: '3d ago',
    scope: 'hop' as const,
    gitPath: 'hopwork/internal',
    status: 'active' as const,
  },
]

const MOCK_ACTIVITY: ActivityEvent[] = [
  {
    id: 'e1',
    dayKey: '2026-07-12',
    dayLabel: 'Saturday, July 12',
    time: '11:48am',
    kind: 'comment',
    actor: 'Lauren',
    isMe: false,
    isTeam: false,
    title: 'Fix Happiness Calendar settings',
    todoId: '9821550339',
    project: 'GGSC',
    projectId: '1',
    linked: false,
    flowStatus: null,
    excerpt:
      'Any update on timing? We need this before the next happiness drop.',
  },
  {
    id: 'e2',
    dayKey: '2026-07-12',
    dayLabel: 'Saturday, July 12',
    time: '10:15am',
    kind: 'updated',
    actor: 'Dan',
    isMe: false,
    isTeam: true,
    title: 'Restructure events page - three sections',
    todoId: '9669131017',
    project: 'GGSC',
    projectId: '1',
    linked: true,
    flowStatus: 'flow ran',
    excerpt: 'status: in_progress · priority: medium · Featured stays editorial',
  },
  {
    id: 'e3',
    dayKey: '2026-07-11',
    dayLabel: 'Friday, July 11',
    time: '4:22pm',
    kind: 'completed',
    actor: 'Alice',
    isMe: false,
    isTeam: true,
    title: 'Update homepage hero copy',
    todoId: '9711001001',
    project: 'GGSC',
    projectId: '1',
    linked: false,
    flowStatus: null,
    excerpt: null,
  },
  {
    id: 'e4',
    dayKey: '2026-07-10',
    dayLabel: 'Thursday, July 10',
    time: '2:05pm',
    kind: 'created',
    actor: 'Rowan',
    isMe: true,
    isTeam: true,
    title: 'Develop proposal to improve Quiz publishing system',
    todoId: '8033530121',
    project: 'GGIA',
    projectId: '2',
    linked: true,
    flowStatus: 'inbox posted',
    excerpt: 'Linked work spec created from Basecamp todo',
  },
  {
    id: 'e5',
    dayKey: '2026-07-09',
    dayLabel: 'Wednesday, July 9',
    time: '9:30am',
    kind: 'comment',
    actor: 'Rowan',
    isMe: true,
    isTeam: true,
    title: 'Weekly product sync notes',
    todoId: '9900112233',
    project: 'Hop Internal',
    projectId: '4',
    linked: false,
    flowStatus: null,
    excerpt: 'Agenda locked for Monday - please add blockers before EOD.',
  },
  {
    id: 'e6',
    dayKey: '2026-07-08',
    dayLabel: 'Tuesday, July 8',
    time: '3:00pm',
    kind: 'updated',
    actor: 'Client PM',
    isMe: false,
    isTeam: false,
    title: 'SIL verification review',
    todoId: '8000000001',
    project: 'Exida',
    projectId: '3',
    linked: true,
    flowStatus: null,
    excerpt: 'Please confirm the verification checklist before Friday.',
  },
]

function formatDue(dueOn: string | null): string {
  if (!dueOn) return 'No due date'
  return new Date(`${dueOn}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function kindLabel(kind: ActivityEvent['kind']): string {
  if (kind === 'comment') return 'Comment'
  if (kind === 'completed') return 'Completed'
  if (kind === 'updated') return 'Updated'
  return 'Created'
}

function activityVerb(kind: ActivityEvent['kind']): string {
  if (kind === 'comment') return 'commented on'
  if (kind === 'completed') return 'completed'
  if (kind === 'updated') return 'updated'
  return 'created'
}

function actorInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const AVATAR_TONES = [
  'bg-sky-600',
  'bg-emerald-600',
  'bg-violet-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-teal-600',
] as const

function avatarTone(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash + name.charCodeAt(i) * 17) % 97
  return AVATAR_TONES[hash % AVATAR_TONES.length]
}

function kindDotClass(kind: ActivityEvent['kind']): string {
  if (kind === 'completed') return 'bg-emerald-500'
  if (kind === 'comment') return 'bg-sky-500'
  if (kind === 'updated') return 'bg-violet-500'
  return 'bg-amber-500'
}

export function BasecampExtensionTest() {
  const [nav, setNav] = useState<{ stack: Scene[]; index: number }>({
    stack: ['browse'],
    index: 0,
  })
  const scene = nav.stack[nav.index] ?? 'browse'
  const canGoBack = nav.index > 0
  const canGoForward = nav.index < nav.stack.length - 1

  const [bcExpanded, setBcExpanded] = useState(true)
  const [bookmarksExpanded, setBookmarksExpanded] = useState(true)
  const [showCompleted, setShowCompleted] = useState(false)
  const [selectedId, setSelectedId] = useState('9821550339')
  const [previewId, setPreviewId] = useState<string | null>('9821550339')
  const [todos, setTodos] = useState(MOCK_TODOS)
  const [projects, setProjects] = useState(MOCK_PROJECTS)
  const [activeProjectId, setActiveProjectId] = useState('1')
  const [activeProjectName, setActiveProjectName] = useState(
    'Greater Good Science Center',
  )
  const [activeListName, setActiveListName] = useState('GGSC - Web')
  const [selectedProjectId, setSelectedProjectId] = useState('1')
  const [projectsPreviewOpen, setProjectsPreviewOpen] = useState(false)
  const [createFolder, setCreateFolder] = useState(
    'hop-studios/clients/greater-good/ggia/todos/',
  )
  const [pulledAt, setPulledAt] = useState<string | null>(null)
  const [harnessNote, setHarnessNote] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)
  const [validated, setValidated] = useState(false)
  const [activityKind, setActivityKind] = useState<'all' | ActivityEvent['kind']>('all')
  const [activityLinkedOnly, setActivityLinkedOnly] = useState(false)
  const [activityWho, setActivityWho] = useState<'org' | 'team' | 'mine'>('org')
  const [activityProjectId, setActivityProjectId] = useState<'all' | string>('all')
  /** Mock Listening health for the combined status control. */
  const [listeningHealth, setListeningHealth] = useState<
    'connected' | 'degraded' | 'disconnected'
  >('connected')

  const selected = useMemo(
    () => todos.find((t) => t.id === selectedId) ?? todos[0],
    [todos, selectedId],
  )

  const visibleTodos = useMemo(
    () => (showCompleted ? todos : todos.filter((t) => !t.completed)),
    [todos, showCompleted],
  )

  const bookmarked = useMemo(() => todos.filter((t) => t.bookmarked), [todos])

  const filteredActivity = useMemo(() => {
    const listeningProjectIds = new Set(
      projects.filter((p) => p.listening).map((p) => p.id),
    )
    return MOCK_ACTIVITY.filter((e) => {
      // Event log only includes Listening projects (ingress drops the rest)
      if (!listeningProjectIds.has(e.projectId)) return false
      if (activityWho === 'mine' && !e.isMe) return false
      if (activityWho === 'team' && !e.isTeam) return false
      if (activityProjectId !== 'all' && e.projectId !== activityProjectId) return false
      if (activityKind !== 'all' && e.kind !== activityKind) return false
      if (activityLinkedOnly && !e.linked) return false
      return true
    })
  }, [
    projects,
    activityWho,
    activityProjectId,
    activityKind,
    activityLinkedOnly,
  ])

  function note(msg: string) {
    setHarnessNote(msg)
    window.setTimeout(() => setHarnessNote(null), 2200)
  }

  function applySceneEffects(next: Scene) {
    if (next === 'create-spec' || next === 'todo-unlinked') {
      setSelectedId('9821550339')
      setTodos((prev) =>
        prev.map((t) => (t.id === '9821550339' ? { ...t, linkedDoc: null } : t)),
      )
      setPulledAt(null)
    }
    if (next === 'todo-empty-notes') {
      setSelectedId('8033530121')
    }
    if (next === 'todo-linked') {
      setSelectedId('9821550339')
      setTodos((prev) =>
        prev.map((t) =>
          t.id === '9821550339'
            ? {
                ...t,
                linkedDoc: {
                  title: 'Fix Happiness Calendar settings',
                  path: 'hop-studios/clients/greater-good/ggia/todos/9821550339-fix-happiness-calendar-settings.md',
                },
              }
            : t,
        ),
      )
      setPulledAt('2026-07-11T23:02:00.000Z')
    }
    if (next === 'project' || next === 'project-todolist') {
      const p = projects.find((x) => x.id === activeProjectId) ?? projects[0]
      setActiveProjectId(p.id)
      setActiveProjectName(p.name)
      if (next === 'project-todolist') {
        const lists = MOCK_LISTS[p.id] ?? []
        if (lists[0]) setActiveListName(lists[0].name)
      }
    }
    if (next === 'settings-connected' || next === 'settings-listening') {
      setValidated(next === 'settings-listening')
    }
    if (next === 'todos-preview') setPreviewId('9821550339')
  }

  /** Push onto prototype history - models per-tab back/forward. */
  function navigateTo(next: Scene, opts?: { effects?: boolean }) {
    setNav((n) => {
      const stack = [...n.stack.slice(0, n.index + 1)]
      if (stack[stack.length - 1] !== next) stack.push(next)
      return { stack, index: stack.length - 1 }
    })
    if (opts?.effects !== false) applySceneEffects(next)
  }

  function goBack() {
    setNav((n) => (n.index <= 0 ? n : { ...n, index: n.index - 1 }))
    note('Back (tab history)')
  }

  function goForward() {
    setNav((n) =>
      n.index >= n.stack.length - 1 ? n : { ...n, index: n.index + 1 },
    )
    note('Forward (tab history)')
  }

  function openTodo(id: string, mode: 'unlinked' | 'linked' | 'empty' = 'unlinked') {
    setSelectedId(id)
    if (mode === 'empty') navigateTo('todo-empty-notes', { effects: false })
    else if (mode === 'linked') navigateTo('todo-linked', { effects: false })
    else navigateTo('todo-unlinked', { effects: false })
  }

  function openProject(projectId: string, projectName?: string) {
    setActiveProjectId(projectId)
    if (projectName) setActiveProjectName(projectName)
    else {
      const p = projects.find((x) => x.id === projectId)
      if (p) setActiveProjectName(p.name)
    }
    navigateTo('project', { effects: false })
  }

  function openTodolist(listName: string) {
    setActiveListName(listName)
    navigateTo('project-todolist', { effects: false })
  }

  function applyScene(next: Scene) {
    navigateTo(next)
  }

  function createWorkSpec() {
    const path = `${createFolder.replace(/\/?$/, '/')}${selected.id}-${slugify(selected.name)}.md`
    setTodos((prev) =>
      prev.map((t) =>
        t.id === selected.id
          ? { ...t, linkedDoc: { title: selected.name, path }, workSpec: 'in-sync' }
          : t,
      ),
    )
    setPulledAt(new Date().toISOString())
    navigateTo('todo-linked')
    note(`Created ${selected.id}-${slugify(selected.name)}.md`)
  }

  function pullLatest() {
    setPulledAt(new Date().toISOString())
    note('Pulled due, assignee, comments (explicit sync)')
  }

  function runValidate() {
    setValidating(true)
    window.setTimeout(() => {
      setValidating(false)
      setValidated(true)
      note('Validated: Basecamp /authorization.json ok')
    }, 600)
  }

  const showAppChrome = ![
    'settings-disconnected',
    'settings-connected',
    'settings-listening',
  ].includes(scene)

  const showCreateDock = scene === 'create-spec'

  return (
    <div
      className="flex h-screen flex-col bg-background text-foreground"
      data-testid={testIds.basecampExtensionTest.root}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2.5">
        <Button variant="ghost" size="sm" asChild>
          <a href="/?test=index">
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Index
          </a>
        </Button>
        <div className="flex items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            disabled={!canGoBack}
            onClick={goBack}
            title="Back (tab history)"
            data-testid={testIds.basecampExtensionTest.navBack}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            disabled={!canGoForward}
            onClick={goForward}
            title="Forward (tab history)"
            data-testid={testIds.basecampExtensionTest.navForward}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Basecamp Extension Chrome v2</div>
          <div className="truncate text-xs text-muted-foreground">
            Full inventory · back/forward = tab history mock ·{' '}
            <a className="underline-offset-2 hover:underline" href="/?test=asana-extension">
              compare Asana
            </a>
            {' · '}
            <a className="underline-offset-2 hover:underline" href="/?test=basecamp-todos">
              todos table proto
            </a>
          </div>
        </div>
        <div
          className="flex max-w-[52%] flex-wrap justify-end gap-1"
          data-testid={testIds.basecampExtensionTest.sceneTabs}
        >
          {SCENES.map((s) => (
            <Button
              key={s.id}
              size="sm"
              variant={scene === s.id ? 'secondary' : 'ghost'}
              className="h-7 text-xs"
              onClick={() => applyScene(s.id)}
            >
              {s.label}
            </Button>
          ))}
        </div>
      </header>

      {harnessNote ? (
        <div
          className="border-b border-border bg-muted/30 px-4 py-1.5 text-xs text-muted-foreground"
          role="status"
        >
          Harness: {harnessNote}
        </div>
      ) : null}

      {(scene === 'settings-disconnected' ||
        scene === 'settings-connected' ||
        scene === 'settings-listening') && (
        <SettingsScenes
          scene={scene}
          validating={validating}
          validated={validated}
          projects={projects}
          onConnect={() => {
            navigateTo('settings-connected')
            setValidated(false)
            note('OAuth complete - validate before save')
          }}
          onValidate={runValidate}
          onOpenListening={() => navigateTo('settings-listening')}
          onToggleListening={(id) => {
            setProjects((prev) =>
              prev.map((p) =>
                p.id === id
                  ? {
                      ...p,
                      listening: !p.listening,
                      lastEvent: !p.listening ? 'just now' : null,
                    }
                  : p,
              ),
            )
            note('Harness: Listening toggle (production = Triggers bet)')
          }}
          onDisconnect={() => {
            navigateTo('settings-disconnected')
            setValidated(false)
          }}
          onOpenBrowse={() => navigateTo('browse')}
          onOpenActivity={() => navigateTo('activity')}
        />
      )}

      {showAppChrome && (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <aside
            className="flex w-[17rem] shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground"
            data-testid={testIds.basecampExtensionTest.browseSidebar}
          >
            <div className="border-b border-sidebar-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Browse
            </div>
            <div className="flex-1 overflow-auto py-1" role="tree">
              <TreeRow
                depth={0}
                label="Hop Studios"
                icon={<FolderOpen className="h-3.5 w-3.5" />}
                expanded
              />
              <TreeRow
                depth={1}
                label="Documents"
                icon={<FileText className="h-3.5 w-3.5" />}
              />
              <TreeRow
                depth={1}
                label="Basecamp"
                icon={<Tent className="h-3.5 w-3.5" />}
                expanded={bcExpanded}
                onToggle={() => setBcExpanded((v) => !v)}
                active
                testId={testIds.basecampExtensionTest.basecampNavRow}
              />
              {bcExpanded && (
                <>
                  <TreeRow
                    depth={2}
                    label="Bookmarks"
                    icon={<Bookmark className="h-3.5 w-3.5" />}
                    expanded={bookmarksExpanded}
                    onToggle={() => setBookmarksExpanded((v) => !v)}
                    onActivate={() => navigateTo('bookmarks')}
                    active={scene === 'bookmarks'}
                    badge={
                      <span className="text-[10px] text-muted-foreground">
                        {bookmarked.length}
                      </span>
                    }
                  />
                  {bookmarksExpanded &&
                    bookmarked.map((t) => (
                      <TreeRow
                        key={`bm-${t.id}`}
                        depth={3}
                        label={t.name}
                        icon={<Bookmark className="h-3 w-3 text-amber-600" />}
                        onActivate={() =>
                          openTodo(
                            t.id,
                            t.id === '8033530121'
                              ? 'empty'
                              : t.linkedDoc
                                ? 'linked'
                                : 'unlinked',
                          )
                        }
                        active={
                          selectedId === t.id && scene.startsWith('todo')
                        }
                        testId={testIds.basecampExtensionTest.todoRow(t.id)}
                      />
                    ))}
                  <TreeRow
                    depth={2}
                    label="Todos"
                    icon={<ListTodo className="h-3.5 w-3.5" />}
                    onActivate={() => navigateTo('todos')}
                    active={scene === 'todos' || scene === 'todos-preview'}
                    badge={
                      <span className="text-[10px] text-muted-foreground">
                        {visibleTodos.filter((t) => t.assignee === 'Rowan').length}
                      </span>
                    }
                  />
                  <TreeRow
                    depth={2}
                    label="Projects"
                    icon={<FolderOpen className="h-3.5 w-3.5" />}
                    onActivate={() => navigateTo('projects')}
                    active={
                      scene === 'projects' ||
                      scene === 'project' ||
                      scene === 'project-todolist'
                    }
                  />
                  <TreeRow
                    depth={2}
                    label="Activity"
                    icon={<Activity className="h-3.5 w-3.5" />}
                    onActivate={() => navigateTo('activity')}
                    active={scene === 'activity'}
                    badge={
                      <span className="text-[10px] text-muted-foreground">vision</span>
                    }
                  />
                </>
              )}
            </div>
            <div className="border-t border-sidebar-border p-2">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
                Show completed
              </label>
            </div>
          </aside>

          <main
            className="relative flex min-w-0 flex-1 overflow-hidden"
            data-testid={testIds.basecampExtensionTest.mainPane}
          >
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              {scene === 'browse' && (
                <TeachingEmpty
                  onOpenTodo={() => openTodo('9821550339')}
                  onOpenActivity={() => navigateTo('activity')}
                />
              )}
              {scene === 'bookmarks' && (
                <BookmarksPanel
                  items={bookmarked}
                  onOpen={(id) => openTodo(id, id === '8033530121' ? 'empty' : 'unlinked')}
                />
              )}
              {(scene === 'todos' || scene === 'todos-preview') && (
                <TodosPanel
                  items={visibleTodos}
                  initialPreviewOpen={scene === 'todos-preview'}
                  selectedId={previewId}
                  onSelect={setPreviewId}
                  onOpen={(id) => openTodo(id)}
                  onPreviewChange={(open) => {
                    navigateTo(open ? 'todos-preview' : 'todos', { effects: false })
                  }}
                />
              )}
              {scene === 'projects' && (
                <ProjectsPanel
                  projects={projects}
                  selectedId={selectedProjectId}
                  initialPreviewOpen={projectsPreviewOpen}
                  onSelect={setSelectedProjectId}
                  onOpen={openProject}
                  onListening={() => navigateTo('settings-listening')}
                  onPreviewChange={setProjectsPreviewOpen}
                  todos={todos}
                />
              )}
              {scene === 'projects-focus' && (
                <ProjectsFocusPanel
                  onOpenListening={() => navigateTo('settings-listening')}
                  onOpenProject={(id, name) => openProject(id, name)}
                />
              )}
              {scene === 'project' && (
                <ProjectPanel
                  project={
                    projects.find((p) => p.id === activeProjectId) ?? projects[0]
                  }
                  todos={todos.filter((t) => t.project === activeProjectName)}
                  lists={MOCK_LISTS[activeProjectId] ?? []}
                  onOpenList={openTodolist}
                  onOpenTodo={(id) => openTodo(id)}
                  onCrumb={(crumb) => {
                    if (crumb === 'projects') navigateTo('projects', { effects: false })
                    if (crumb === 'basecamp') navigateTo('browse', { effects: false })
                  }}
                  onListening={() => navigateTo('settings-listening')}
                  onNote={note}
                />
              )}
              {scene === 'project-todolist' && (
                <TodolistPanel
                  projectName={activeProjectName}
                  listName={activeListName}
                  todos={todos.filter(
                    (t) =>
                      t.project === activeProjectName && t.list === activeListName,
                  )}
                  onOpen={(id) => openTodo(id)}
                  onCrumb={(crumb) => {
                    if (crumb === 'project') navigateTo('project', { effects: false })
                    if (crumb === 'projects') navigateTo('projects', { effects: false })
                    if (crumb === 'basecamp') navigateTo('browse', { effects: false })
                  }}
                />
              )}
              {scene === 'activity' && (
                <ActivityPanel
                  events={filteredActivity}
                  projects={projects}
                  kind={activityKind}
                  linkedOnly={activityLinkedOnly}
                  who={activityWho}
                  projectId={activityProjectId}
                  listeningHealth={listeningHealth}
                  onKind={setActivityKind}
                  onLinkedOnly={setActivityLinkedOnly}
                  onWho={setActivityWho}
                  onProjectId={setActivityProjectId}
                  onOpen={(id) => openTodo(id)}
                  onListening={() => navigateTo('settings-listening')}
                  onCycleListeningHealth={() => {
                    setListeningHealth((h) =>
                      h === 'connected'
                        ? 'degraded'
                        : h === 'degraded'
                          ? 'disconnected'
                          : 'connected',
                    )
                    note('Cycled Listening status (prototype)')
                  }}
                />
              )}
              {(scene === 'todo-unlinked' ||
                scene === 'todo-linked' ||
                scene === 'todo-empty-notes' ||
                scene === 'create-spec') && (
                <TodoViewer
                  todo={selected}
                  forceEmptyNotes={scene === 'todo-empty-notes'}
                  pulledAt={pulledAt}
                  onOpenCreate={() => navigateTo('create-spec')}
                  onPull={pullLatest}
                  onNote={note}
                  onToggleBookmark={() => {
                    setTodos((prev) =>
                      prev.map((t) =>
                        t.id === selected.id
                          ? { ...t, bookmarked: !t.bookmarked }
                          : t,
                      ),
                    )
                    note(
                      selected.bookmarked
                        ? 'Removed bookmark'
                        : 'Bookmarked todo',
                    )
                  }}
                  onUnlink={() => {
                    setTodos((prev) =>
                      prev.map((t) =>
                        t.id === selected.id ? { ...t, linkedDoc: null } : t,
                      ),
                    )
                    navigateTo('todo-unlinked')
                    note('Unlinked document')
                  }}
                />
              )}
            </div>

            <div
              className="grid shrink-0 transition-[grid-template-columns] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{
                gridTemplateColumns: showCreateDock ? '20rem' : '0fr',
              }}
            >
              <div className="min-w-0 overflow-hidden">
                <aside
                  className="flex h-full w-80 flex-col border-l border-border bg-background"
                  data-testid={testIds.basecampExtensionTest.createSpecDialog}
                >
                  <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
                    <div>
                      <h2 className="text-sm font-semibold">Create work spec</h2>
                      <p className="text-xs text-muted-foreground">
                        From Basecamp · todo_id prefix
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => navigateTo('todo-unlinked')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex-1 space-y-4 overflow-auto px-4 py-4 text-sm">
                    <Field label="Title" value={selected.name} readOnly />
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        Folder
                      </label>
                      <Input
                        value={createFolder}
                        onChange={(e) => setCreateFolder(e.target.value)}
                      />
                    </div>
                    <Field
                      label="Filename"
                      value={`${selected.id}-${slugify(selected.name)}.md`}
                      hint="Basecamp keeps todo_id prefix (unlike Asana slug-only)"
                      readOnly
                    />
                    <pre className="overflow-x-auto whitespace-pre-wrap border-t border-border pt-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{`todo_id: "${selected.id}"
basecamp_todo_id: "${selected.id}"
source_url: "https://app.basecamp.com/5744549/buckets/39953224/todos/${selected.id}"`}
                    </pre>
                  </div>
                  <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigateTo('todo-unlinked')}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      data-testid={testIds.basecampExtensionTest.createSpecConfirm}
                      onClick={createWorkSpec}
                    >
                      Create
                    </Button>
                  </div>
                </aside>
              </div>
            </div>
          </main>
        </div>
      )}
    </div>
  )
}

function SettingsScenes({
  scene,
  validating,
  validated,
  projects,
  onConnect,
  onValidate,
  onOpenListening,
  onToggleListening,
  onDisconnect,
  onOpenBrowse,
  onOpenActivity,
}: {
  scene: Scene
  validating: boolean
  validated: boolean
  projects: typeof MOCK_PROJECTS
  onConnect: () => void
  onValidate: () => void
  onOpenListening: () => void
  onToggleListening: (id: string) => void
  onDisconnect: () => void
  onOpenBrowse: () => void
  onOpenActivity: () => void
}) {
  return (
    <div className="flex flex-1 overflow-hidden">
      <aside className="w-52 shrink-0 border-r border-border bg-sidebar p-3 text-sidebar-foreground">
        <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Settings
        </div>
        {['General', 'Extensions', 'Secrets'].map((item) => (
          <div
            key={item}
            className={cn(
              'px-2 py-1.5 text-sm',
              item === 'Extensions' && 'bg-sidebar-accent font-medium',
            )}
          >
            {item}
          </div>
        ))}
      </aside>
      <main
        className="flex-1 overflow-auto px-10 py-9"
        data-testid={
          scene === 'settings-listening'
            ? testIds.basecampExtensionTest.listeningPanel
            : testIds.basecampExtensionTest.settingsPanel
        }
      >
        <div className="mx-auto max-w-lg space-y-8">
          <div>
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">Basecamp</h1>
              <span className="text-xs text-muted-foreground">
                {scene === 'settings-disconnected' ? 'Disabled' : 'Enabled'}
              </span>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Reach into Basecamp from this workspace. Todos stay in Basecamp; Maater
              links work specs and pulls on demand.
            </p>
          </div>

          {scene === 'settings-disconnected' ? (
            <div className="space-y-4 border-t border-border pt-6">
              <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Not connected. Tokens go in user_extension_tokens.</span>
              </div>
              <Button
                data-testid={testIds.basecampExtensionTest.connectButton}
                onClick={onConnect}
              >
                Connect Basecamp
              </Button>
            </div>
          ) : (
            <div className="space-y-6 border-t border-border pt-6">
              <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                <Check className="h-4 w-4" />
                Connected as Rowan Sentesy
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  Linked Basecamp account
                </label>
                <div className="border-b border-input py-2 text-sm">
                  Hop Studios (5744549)
                </div>
              </div>
              {scene !== 'settings-listening' && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Credential check
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={validating}
                      onClick={onValidate}
                    >
                      {validating ? 'Validating…' : 'Validate'}
                    </Button>
                    {validated ? (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        OK
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Required before Save
                      </span>
                    )}
                  </div>
                </div>
              )}

              {(scene === 'settings-listening' || validated) && (
                <div className="space-y-4 border-t border-border pt-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="flex items-center gap-2 text-sm font-semibold">
                        <Radio className="h-4 w-4" />
                        Listening
                      </h2>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Vision mock - production ships in Triggers / Listening bet.
                        Project-scoped Basecamp webhooks; Activity reads the event log.
                      </p>
                    </div>
                    {scene !== 'settings-listening' && (
                      <Button size="sm" variant="secondary" onClick={onOpenListening}>
                        Manage
                      </Button>
                    )}
                  </div>
                  <div className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground">
                    Endpoint healthy · Last delivery 12m ago · Types: Todo, Comment
                  </div>
                  <ul className="divide-y divide-border border-y border-border">
                    {projects.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center gap-3 py-3 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.listening
                              ? `Listening · last event ${p.lastEvent ?? '—'}`
                              : 'Off'}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={p.listening ? 'secondary' : 'outline'}
                          className="h-7"
                          onClick={() => onToggleListening(p.id)}
                        >
                          {p.listening ? 'On' : 'Off'}
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" variant="link" className="px-0" onClick={onOpenActivity}>
                    Open Activity feed
                  </Button>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button size="sm" disabled={!validated && scene !== 'settings-listening'}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={onDisconnect}>
                  Disconnect
                </Button>
                <Button size="sm" variant="link" className="px-0" onClick={onOpenBrowse}>
                  Open Browse
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

function TeachingEmpty({
  onOpenTodo,
  onOpenActivity,
}: {
  onOpenTodo: () => void
  onOpenActivity: () => void
}) {
  return (
    <div className="flex flex-1 flex-col justify-center px-10 py-12">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Basecamp in this workspace
      </p>
      <h2 className="mb-6 max-w-md text-2xl font-semibold tracking-tight">
        Work stays in Basecamp. Context compounds here.
      </h2>
      <ol className="max-w-sm space-y-3 text-sm text-muted-foreground">
        <li>
          <span className="font-medium text-foreground">1.</span> Open Todos, Bookmarks,
          or Projects under Basecamp.
        </li>
        <li>
          <span className="font-medium text-foreground">2.</span> Preview a todo - it is
          not a Maater document until you link or create a work spec.
        </li>
        <li>
          <span className="font-medium text-foreground">3.</span> Enable Listening in
          Settings to fill the Activity feed (event log - not Inbox).
        </li>
      </ol>
      <div className="mt-8 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={onOpenTodo}>
          Open "Fix Happiness Calendar settings"
        </Button>
        <Button variant="ghost" onClick={onOpenActivity}>
          Preview Activity
        </Button>
      </div>
    </div>
  )
}

function PreviewPanelToggle({
  open,
  onToggle,
  testId,
}: {
  open: boolean
  onToggle: () => void
  testId?: string
}) {
  return (
    <Button
      variant={open ? 'secondary' : 'ghost'}
      size="sm"
      className="h-8 w-8 shrink-0 p-0"
      title={open ? 'Hide preview panel' : 'Show preview panel'}
      data-testid={testId}
      onClick={onToggle}
    >
      <PanelRight className="h-4 w-4" />
    </Button>
  )
}

function BookmarksPanel({
  items,
  onOpen,
}: {
  items: BcTodo[]
  onOpen: (id: string) => void
}) {
  const [selectedId, setSelectedId] = useState<string | null>(items[0]?.id ?? null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [ctx, setCtx] = useState<{ x: number; y: number; id: string } | null>(null)
  const selected = items.find((t) => t.id === selectedId) ?? null

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={testIds.basecampExtensionTest.bookmarksPanel}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Bookmarks</h1>
          <p className="text-xs text-muted-foreground">
            Pinned todos you care about - not a dump of overdue assignments.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{items.length}</span>
          <PreviewPanelToggle
            open={previewOpen}
            onToggle={() => {
              const next = !previewOpen
              setPreviewOpen(next)
              if (next && !selectedId && items[0]) setSelectedId(items[0].id)
            }}
            testId={testIds.basecampExtensionTest.bookmarksPreviewToggle}
          />
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <ul className="flex-1 overflow-auto">
          {items.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => setSelectedId(t.id)}
                onDoubleClick={() => onOpen(t.id)}
                onContextMenu={(e) => {
                  e.preventDefault()
                  setSelectedId(t.id)
                  setCtx({ x: e.clientX, y: e.clientY, id: t.id })
                }}
                className={cn(
                  'flex w-full items-start gap-3 border-b border-border/60 px-6 py-3.5 text-left hover:bg-accent/40',
                  selectedId === t.id && 'bg-accent/50',
                )}
              >
                <Bookmark className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{t.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t.project}
                    <span className="mx-1.5 opacity-40">·</span>
                    {t.list}
                  </div>
                </div>
                <div className="shrink-0 text-right text-xs text-muted-foreground">
                  {t.daysOverdue ? (
                    <span className="text-red-500">{t.daysOverdue}d overdue</span>
                  ) : (
                    formatDue(t.dueOn)
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
        {previewOpen && (
          <aside className="flex w-72 shrink-0 flex-col border-l border-border">
            {selected ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </p>
                <h2 className="mt-2 text-sm font-semibold leading-snug">{selected.name}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {selected.project} · {selected.list}
                </p>
                <p className="mt-4 line-clamp-6 text-sm text-muted-foreground">
                  {selected.notes || 'No notes on this todo.'}
                </p>
                <Button size="sm" className="mt-auto" onClick={() => onOpen(selected.id)}>
                  Open todo
                </Button>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-muted-foreground">
                Select a bookmark to preview
              </div>
            )}
          </aside>
        )}
      </div>
      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          onClose={() => setCtx(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                setSelectedId(ctx.id)
                setPreviewOpen(true)
              },
            },
            {
              label: 'Open',
              icon: ExternalLink,
              onSelect: () => onOpen(ctx.id),
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function TodosPanel({
  items,
  initialPreviewOpen,
  selectedId,
  onSelect,
  onOpen,
  onPreviewChange,
}: {
  items: BcTodo[]
  initialPreviewOpen: boolean
  selectedId: string | null
  onSelect: (id: string) => void
  onOpen: (id: string) => void
  onPreviewChange: (open: boolean) => void
}) {
  const [scope, setScope] = useState<'all' | 'hop_only' | 'client_only'>('client_only')
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'overdue_and_today' | 'overdue' | 'due_today'
  >('all')
  const [query, setQuery] = useState('')
  const [previewOpen, setPreviewOpen] = useState(initialPreviewOpen)
  const [ctx, setCtx] = useState<{ x: number; y: number; id: string } | null>(null)
  const cachedAt = '7/12/2026, 12:53:52 AM'

  useEffect(() => {
    setPreviewOpen(initialPreviewOpen)
  }, [initialPreviewOpen])

  const today = '2026-07-12'

  const filtered = useMemo(() => {
    return items.filter((t) => {
      if (scope === 'hop_only' && !t.isHop) return false
      if (scope === 'client_only' && t.isHop) return false

      const overdue = Boolean(t.daysOverdue && t.daysOverdue > 0)
      const dueToday = t.dueOn === today
      if (statusFilter === 'overdue' && !overdue) return false
      if (statusFilter === 'due_today' && !dueToday) return false
      if (statusFilter === 'overdue_and_today' && !overdue && !dueToday) return false

      if (query.trim()) {
        const q = query.toLowerCase()
        const hay = `${t.name} ${t.project} ${t.list}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [items, scope, statusFilter, query, today])

  const previewTodo = items.find((t) => t.id === selectedId) ?? null

  function togglePreview() {
    const next = !previewOpen
    setPreviewOpen(next)
    onPreviewChange(next)
    if (next && !selectedId && filtered[0]) onSelect(filtered[0].id)
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={testIds.basecampExtensionTest.todosPanel}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold tracking-tight">Todos</h1>
          <Badge variant="secondary" className="font-normal">
            {filtered.length}
          </Badge>
        </div>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          {cachedAt}
        </span>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-muted/20 px-6 py-2">
        <Tabs value={scope} onValueChange={(v) => setScope(v as typeof scope)}>
          <TabsList className="h-8">
            <TabsTrigger value="all" className="h-7 px-3 text-xs">
              All
            </TabsTrigger>
            <TabsTrigger value="hop_only" className="h-7 px-3 text-xs">
              Hop internal
            </TabsTrigger>
            <TabsTrigger value="client_only" className="h-7 px-3 text-xs">
              Client
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
        >
          <TabsList className="h-8">
            <TabsTrigger value="all" className="h-7 px-3 text-xs">
              All
            </TabsTrigger>
            <TabsTrigger value="overdue_and_today" className="h-7 px-3 text-xs">
              Overdue + Due today
            </TabsTrigger>
            <TabsTrigger value="overdue" className="h-7 px-3 text-xs">
              Overdue
            </TabsTrigger>
            <TabsTrigger value="due_today" className="h-7 px-3 text-xs">
              Due today
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative min-w-[160px] max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="h-8 pl-8 text-sm"
          />
        </div>
        <PreviewPanelToggle
          open={previewOpen}
          onToggle={togglePreview}
          testId={testIds.basecampExtensionTest.todosPreviewToggle}
        />
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="grid grid-cols-[7rem_minmax(0,1.4fr)_minmax(0,0.9fr)_4.5rem_minmax(0,1fr)_5.5rem] gap-2 border-b border-border px-6 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span>Project</span>
            <span>Title</span>
            <span>List</span>
            <span>Spec</span>
            <span>Activity</span>
            <span>Due</span>
          </div>
          {filtered.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {query ? 'No matching todos' : 'No todos in this filter'}
            </div>
          ) : (
            <ul className="flex-1 overflow-auto">
              {filtered.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(t.id)}
                    onDoubleClick={() => onOpen(t.id)}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      onSelect(t.id)
                      setCtx({ x: e.clientX, y: e.clientY, id: t.id })
                    }}
                    className={cn(
                      'grid w-full grid-cols-[7rem_minmax(0,1.4fr)_minmax(0,0.9fr)_4.5rem_minmax(0,1fr)_5.5rem] gap-2 border-b border-border/50 px-6 py-2.5 text-left text-sm hover:bg-accent/40',
                      selectedId === t.id && 'bg-accent/50',
                    )}
                    data-testid={testIds.basecampExtensionTest.todoRow(t.id)}
                  >
                    <span className="truncate text-xs text-muted-foreground">
                      {t.isHop ? 'Hop' : t.project.split(' ')[0]}
                    </span>
                    <span className="truncate font-medium">{t.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{t.list}</span>
                    <span className="text-xs text-muted-foreground">
                      {t.workSpec === 'in-sync'
                        ? 'In sync'
                        : t.workSpec === 'stale'
                          ? 'Out of date'
                          : 'no spec'}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {t.activity ?? '—'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t.daysOverdue ? (
                        <span className="text-red-500">{t.daysOverdue}d overdue</span>
                      ) : (
                        formatDue(t.dueOn)
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {previewOpen && (
          <aside
            className="flex w-72 shrink-0 flex-col border-l border-border"
            data-testid={testIds.basecampExtensionTest.todosPreview}
          >
            {previewTodo ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </p>
                <h2 className="mt-2 text-sm font-semibold leading-snug">
                  {previewTodo.name}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {previewTodo.assignee} · Due {formatDue(previewTodo.dueOn)}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {previewTodo.notes || 'No notes on this todo.'}
                </p>
                <Button
                  size="sm"
                  className="mt-auto"
                  onClick={() => onOpen(previewTodo.id)}
                >
                  Open todo
                </Button>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-muted-foreground">
                Select a todo to preview
              </div>
            )}
          </aside>
        )}
      </div>
      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          onClose={() => setCtx(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                onSelect(ctx.id)
                setPreviewOpen(true)
                onPreviewChange(true)
              },
            },
            {
              label: 'Open',
              icon: ExternalLink,
              onSelect: () => onOpen(ctx.id),
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function ProjectsPanel({
  projects,
  selectedId,
  initialPreviewOpen,
  onSelect,
  onOpen,
  onListening,
  onPreviewChange,
  todos,
}: {
  projects: typeof MOCK_PROJECTS
  selectedId: string
  initialPreviewOpen: boolean
  onSelect: (id: string) => void
  onOpen: (projectId: string) => void
  onListening: () => void
  onPreviewChange: (open: boolean) => void
  todos: BcTodo[]
}) {
  const [query, setQuery] = useState('')
  const [listening, setListening] = useState<'all' | 'on' | 'off'>('all')
  const [scope, setScope] = useState<'all' | 'client' | 'hop'>('all')
  const [previewOpen, setPreviewOpen] = useState(initialPreviewOpen)
  const [ctx, setCtx] = useState<{ x: number; y: number; id: string } | null>(null)

  useEffect(() => {
    setPreviewOpen(initialPreviewOpen)
  }, [initialPreviewOpen])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return projects.filter((p) => {
      if (listening === 'on' && !p.listening) return false
      if (listening === 'off' && p.listening) return false
      if (scope !== 'all' && p.scope !== scope) return false
      if (q && !p.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [projects, query, listening, scope])

  const previewProject = projects.find((p) => p.id === selectedId) ?? null
  const projectTodos = todos.filter((t) => t.project === previewProject?.name)

  function togglePreview() {
    const next = !previewOpen
    setPreviewOpen(next)
    onPreviewChange(next)
    if (next && !selectedId && filtered[0]) onSelect(filtered[0].id)
  }

  const listeningFilters: { id: 'all' | 'on' | 'off'; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'on', label: 'Listening' },
    { id: 'off', label: 'Off' },
  ]

  const scopeFilters: { id: 'all' | 'client' | 'hop'; label: string }[] = [
    { id: 'all', label: 'All orgs' },
    { id: 'client', label: 'Client' },
    { id: 'hop', label: 'Hop' },
  ]

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={testIds.basecampExtensionTest.projectsPanel}
    >
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <nav
              className="mb-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
              aria-label="Breadcrumb"
            >
              <span className="font-medium text-foreground">Basecamp</span>
              <span className="opacity-40">/</span>
              <span className="font-medium text-foreground">Projects</span>
            </nav>
            <h1 className="text-xl font-semibold tracking-tight">Projects</h1>
            <p className="text-xs text-muted-foreground">
              Click selects · double-click opens · right-click Preview (same as Todos).
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onListening}>
            Listening settings
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {scopeFilters.map((f) => (
            <Button
              key={f.id}
              size="sm"
              variant={scope === f.id ? 'secondary' : 'ghost'}
              className="h-7 text-xs"
              onClick={() => setScope(f.id)}
            >
              {f.label}
            </Button>
          ))}
          <div className="relative min-w-[12rem] flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter projects..."
              className="h-7 pl-7 text-xs"
              aria-label="Filter projects"
            />
          </div>
          <PreviewPanelToggle
            open={previewOpen}
            onToggle={togglePreview}
            testId={testIds.basecampExtensionTest.projectsPreviewToggle}
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {listeningFilters.map((f) => (
            <Button
              key={f.id}
              size="sm"
              variant={listening === f.id ? 'secondary' : 'ghost'}
              className="h-7 text-xs"
              onClick={() => setListening(f.id)}
            >
              {f.label}
            </Button>
          ))}
          <span className="text-[11px] text-muted-foreground">
            {filtered.length} shown
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {filtered.length === 0 ? (
          <div className="flex flex-1 flex-col justify-center px-10 py-12">
            <h2 className="text-lg font-semibold">No projects in this filter</h2>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Widen scope / Listening filters, or clear the search.
            </p>
          </div>
        ) : (
          <ul className="flex-1 overflow-auto">
            {filtered.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onSelect(p.id)}
                  onDoubleClick={() => onOpen(p.id)}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    onSelect(p.id)
                    setCtx({ x: e.clientX, y: e.clientY, id: p.id })
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 border-b border-border/60 px-6 py-3.5 text-left hover:bg-accent/40',
                    selectedId === p.id && 'bg-accent/50',
                  )}
                >
                  <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.listening
                        ? `Listening · last event ${p.lastEvent}`
                        : 'Not listening - enable in Settings'}
                    </div>
                  </div>
                  {p.listening ? (
                    <Badge variant="secondary" className="font-normal">
                      On
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="font-normal">
                      Off
                    </Badge>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {previewOpen && (
          <aside
            className="flex w-80 shrink-0 flex-col border-l border-border"
            data-testid={testIds.basecampExtensionTest.projectsPreview}
          >
            {previewProject ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </p>
                <h2 className="mt-2 text-sm font-semibold leading-snug">
                  {previewProject.name}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {previewProject.listening ? (
                    <Badge variant="secondary" className="gap-1 font-normal">
                      <Radio className="h-3 w-3" />
                      Listening
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="font-normal">
                      Not listening
                    </Badge>
                  )}
                  <Badge variant="outline" className="font-normal capitalize">
                    {previewProject.scope}
                  </Badge>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  {previewProject.listening
                    ? `Last event ${previewProject.lastEvent ?? 'unknown'}`
                    : 'Enable Listening in Settings to fill Activity for this project.'}
                </p>
                <div className="mt-4 space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Todos in mock ({projectTodos.length})
                  </p>
                  {projectTodos.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No mock todos for this project.</p>
                  ) : (
                    projectTodos.slice(0, 5).map((t) => (
                      <p key={t.id} className="truncate text-xs text-muted-foreground">
                        {t.name}
                      </p>
                    ))
                  )}
                </div>
                <Button
                  size="sm"
                  className="mt-auto"
                  onClick={() => onOpen(previewProject.id)}
                >
                  Open project
                </Button>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-muted-foreground">
                Select a project to preview
              </div>
            )}
          </aside>
        )}
      </div>

      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          onClose={() => setCtx(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                onSelect(ctx.id)
                setPreviewOpen(true)
                onPreviewChange(true)
              },
            },
            {
              label: 'Open',
              icon: FolderOpen,
              onSelect: () => onOpen(ctx.id),
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function ProjectPanel({
  project,
  todos,
  lists,
  onOpenList,
  onOpenTodo,
  onCrumb,
  onListening,
  onNote,
}: {
  project: (typeof MOCK_PROJECTS)[number]
  todos: BcTodo[]
  lists: { id: string; name: string }[]
  onOpenList: (listName: string) => void
  onOpenTodo: (id: string) => void
  onCrumb: (crumb: 'basecamp' | 'projects') => void
  onListening: () => void
  onNote: (msg: string) => void
}) {
  const [selectedListId, setSelectedListId] = useState<string | null>(
    lists[0]?.id ?? null,
  )
  const [previewOpen, setPreviewOpen] = useState(true)
  const [promoted, setPromoted] = useState<'mine' | 'activity' | 'actions' | null>(
    null,
  )
  const [ctx, setCtx] = useState<{ x: number; y: number; listId: string } | null>(
    null,
  )

  const openTodos = todos.filter((t) => !t.completed)
  const overdue = openTodos.filter((t) => (t.daysOverdue ?? 0) > 0 || (t.dueOn && t.dueOn < '2026-07-12'))
  const myTodos = openTodos.filter((t) => t.assignee === 'Rowan')
  const linkedSpecs = todos.filter((t) => t.workSpec && t.workSpec !== 'none').length
  const selectedList = lists.find((l) => l.id === selectedListId) ?? null
  const listTodos = selectedList
    ? todos.filter((t) => t.list === selectedList.name)
    : []

  const listCounts = useMemo(() => {
    const map = new Map<string, { active: number; total: number }>()
    for (const list of lists) {
      const inList = todos.filter((t) => t.list === list.name)
      map.set(list.id, {
        active: inList.filter((t) => !t.completed).length,
        total: inList.length,
      })
    }
    return map
  }, [lists, todos])

  const sideOpen = previewOpen || promoted !== null

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={testIds.basecampExtensionTest.projectPanel}
    >
      <div className="shrink-0 border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <nav
              className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
              aria-label="Breadcrumb"
              data-testid={testIds.basecampExtensionTest.projectsBreadcrumb}
            >
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => onCrumb('basecamp')}
              >
                Basecamp
              </button>
              <span className="opacity-40">/</span>
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => onCrumb('projects')}
              >
                Projects
              </button>
              <span className="opacity-40">/</span>
              <span className="font-medium text-foreground">{project.name}</span>
            </nav>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight">{project.name}</h1>
              <Badge
                variant="outline"
                className="border-green-500/20 bg-green-500/10 text-green-600 text-[10px]"
              >
                Active
              </Badge>
            </div>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={onListening}
                title="Listening settings"
              >
                <Radio className="h-3 w-3" />
                Listening {project.listening ? 'on' : 'off'}
              </button>
              <span>
                Last event {project.lastEvent ?? 'never'}
              </span>
              <span className="inline-flex items-center gap-1">
                {project.gitPath ? (
                  <>
                    <FileText className="h-3 w-3" />
                    {project.gitPath}
                  </>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">
                    No git path configured
                  </span>
                )}
              </span>
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              <span className={overdue.length ? 'text-red-500' : undefined}>
                {overdue.length} overdue
              </span>
              <span className="mx-1.5 opacity-40">·</span>
              {openTodos.length} open
              <span className="mx-1.5 opacity-40">·</span>
              {linkedSpecs} specs
              <span className="mx-1.5 opacity-40">·</span>
              {myTodos.length} mine
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => onNote('Synced project (mock)')}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Sync
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={() => onNote('Open in Basecamp (mock)')}
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Basecamp
            </Button>
            <PreviewPanelToggle
              open={sideOpen}
              onToggle={() => {
                if (sideOpen) {
                  setPreviewOpen(false)
                  setPromoted(null)
                } else {
                  setPreviewOpen(true)
                }
              }}
              testId={testIds.basecampExtensionTest.projectPreviewToggle}
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col overflow-auto">
          <section className="border-b border-border px-2 py-3">
            <div className="mb-1 flex items-center justify-between px-4">
              <h2 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Todo lists
              </h2>
              <span className="text-xs text-muted-foreground">{lists.length}</span>
            </div>
            <ul>
              {lists.map((list) => {
                const counts = listCounts.get(list.id) ?? { active: 0, total: 0 }
                return (
                  <li key={list.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedListId(list.id)
                        setPreviewOpen(true)
                        setPromoted(null)
                      }}
                      onDoubleClick={() => onOpenList(list.name)}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        setSelectedListId(list.id)
                        setCtx({ x: e.clientX, y: e.clientY, listId: list.id })
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-4 py-2.5 text-left text-sm hover:bg-accent/40',
                        selectedListId === list.id && previewOpen && !promoted
                          ? 'bg-accent/50'
                          : '',
                      )}
                    >
                      <ListTodo className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {list.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {counts.active} open
                        {counts.total !== counts.active
                          ? ` · ${counts.total} total`
                          : ''}
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                    </button>
                  </li>
                )
              })}
            </ul>
          </section>

          <div className="space-y-1 p-3">
            <MinimizedSection
              title="My todos"
              count={myTodos.length}
              icon={User}
              onClick={() => {
                setPromoted('mine')
                setPreviewOpen(true)
              }}
            />
            <MinimizedSection
              title="Recent activity"
              count={3}
              icon={Activity}
              onClick={() => {
                setPromoted('activity')
                setPreviewOpen(true)
              }}
            />
            <MinimizedSection
              title="Actions"
              count={3}
              icon={MoreHorizontal}
              onClick={() => {
                setPromoted('actions')
                setPreviewOpen(true)
              }}
            />
          </div>
        </div>

        {sideOpen && (
          <aside
            className="flex w-80 shrink-0 flex-col border-l border-border"
            data-testid={testIds.basecampExtensionTest.projectSecondaryPanel}
          >
            {promoted === 'mine' ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  My todos in {project.name}
                </p>
                {myTodos.length === 0 ? (
                  <p className="mt-6 text-center text-xs text-muted-foreground">
                    None assigned to you in this project.
                  </p>
                ) : (
                  <ul className="mt-3 space-y-1">
                    {myTodos.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          className="w-full rounded-md px-2 py-2 text-left text-sm hover:bg-accent/40"
                          onClick={() => onOpenTodo(t.id)}
                        >
                          <span className="block truncate font-medium">{t.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {t.list} · Due {formatDue(t.dueOn)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : promoted === 'activity' ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Recent activity
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Vision-only until Triggers ships. Sample rows for layout:
                </p>
                <ul className="mt-4 space-y-3 text-sm">
                  <li>
                    <p className="font-medium">Rowan commented</p>
                    <p className="text-xs text-muted-foreground">
                      on Fix Happiness Calendar · 2h ago
                    </p>
                  </li>
                  <li>
                    <p className="font-medium">Lauren completed</p>
                    <p className="text-xs text-muted-foreground">
                      Update homepage hero · Yesterday
                    </p>
                  </li>
                  <li>
                    <p className="font-medium">Alice updated</p>
                    <p className="text-xs text-muted-foreground">
                      Quiz publishing proposal · 3d ago
                    </p>
                  </li>
                </ul>
              </div>
            ) : promoted === 'actions' ? (
              <div className="flex flex-1 flex-col gap-2 overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Actions
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start"
                  onClick={() => onNote('Synced project (mock)')}
                >
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  Sync all project data
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start"
                  onClick={onListening}
                >
                  <Radio className="mr-2 h-3.5 w-3.5" />
                  Listening settings
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start"
                  onClick={() =>
                    onNote(
                      project.gitPath
                        ? `Edit git path: ${project.gitPath}`
                        : 'Configure git path',
                    )
                  }
                >
                  <FileText className="mr-2 h-3.5 w-3.5" />
                  {project.gitPath ? 'Edit git path' : 'Set git path'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start"
                  onClick={() => onNote('Open in Basecamp (mock)')}
                >
                  <ExternalLink className="mr-2 h-3.5 w-3.5" />
                  View in Basecamp
                </Button>
              </div>
            ) : selectedList ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  List preview
                </p>
                <h2 className="mt-2 text-sm font-semibold">{selectedList.name}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {listTodos.filter((t) => !t.completed).length} open ·{' '}
                  {listTodos.length} total
                </p>
                {listTodos.length === 0 ? (
                  <p className="mt-6 text-center text-xs text-muted-foreground">
                    No todos in this list yet.
                  </p>
                ) : (
                  <ul className="mt-3 space-y-1">
                    {listTodos.slice(0, 8).map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/40"
                          onClick={() => onOpenTodo(t.id)}
                        >
                          {t.completed ? (
                            <Check className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <Square className="h-3.5 w-3.5" />
                          )}
                          <span className="min-w-0 flex-1 truncate">{t.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  size="sm"
                  className="mt-auto"
                  onClick={() => onOpenList(selectedList.name)}
                >
                  Open list
                </Button>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-muted-foreground">
                Select a todo list to preview
              </div>
            )}
          </aside>
        )}
      </div>

      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          onClose={() => setCtx(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                setSelectedListId(ctx.listId)
                setPreviewOpen(true)
                setPromoted(null)
              },
            },
            {
              label: 'Open',
              icon: FolderOpen,
              onSelect: () => {
                const list = lists.find((l) => l.id === ctx.listId)
                if (list) onOpenList(list.name)
              },
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function TodolistPanel({
  projectName,
  listName,
  todos,
  onOpen,
  onCrumb,
}: {
  projectName: string
  listName: string
  todos: BcTodo[]
  onOpen: (id: string) => void
  onCrumb: (crumb: 'basecamp' | 'projects' | 'project') => void
}) {
  const [selectedId, setSelectedId] = useState<string | null>(todos[0]?.id ?? null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [ctx, setCtx] = useState<{ x: number; y: number; id: string } | null>(null)
  const selected = todos.find((t) => t.id === selectedId) ?? null

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={testIds.basecampExtensionTest.todolistPanel}
    >
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <nav
              className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
              aria-label="Breadcrumb"
              data-testid={testIds.basecampExtensionTest.projectsBreadcrumb}
            >
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => onCrumb('basecamp')}
              >
                Basecamp
              </button>
              <span className="opacity-40">/</span>
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => onCrumb('projects')}
              >
                Projects
              </button>
              <span className="opacity-40">/</span>
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => onCrumb('project')}
              >
                {projectName}
              </button>
              <span className="opacity-40">/</span>
              <span className="font-medium text-foreground">{listName}</span>
            </nav>
            <h1 className="mt-1 text-xl font-semibold tracking-tight">{listName}</h1>
            <p className="text-xs text-muted-foreground">{projectName}</p>
          </div>
          <PreviewPanelToggle
            open={previewOpen}
            onToggle={() => {
              const next = !previewOpen
              setPreviewOpen(next)
              if (next && !selectedId && todos[0]) setSelectedId(todos[0].id)
            }}
            testId={testIds.basecampExtensionTest.todolistPreviewToggle}
          />
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <ul className="flex-1 overflow-auto px-2 py-2">
          {todos.length === 0 ? (
            <li className="px-4 py-8 text-sm text-muted-foreground">
              No todos in {listName} for {projectName}.
            </li>
          ) : (
            todos.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(t.id)}
                  onDoubleClick={() => onOpen(t.id)}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    setSelectedId(t.id)
                    setCtx({ x: e.clientX, y: e.clientY, id: t.id })
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-4 py-2.5 text-left text-sm hover:bg-accent/40',
                    selectedId === t.id && 'bg-accent/50',
                  )}
                >
                  {t.completed ? (
                    <Check className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{t.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDue(t.dueOn)}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
        {previewOpen && (
          <aside className="flex w-72 shrink-0 flex-col border-l border-border">
            {selected ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </p>
                <h2 className="mt-2 text-sm font-semibold leading-snug">{selected.name}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Due {formatDue(selected.dueOn)} · {selected.assignee ?? 'Unassigned'}
                </p>
                <p className="mt-4 line-clamp-6 text-sm text-muted-foreground">
                  {selected.notes || 'No notes on this todo.'}
                </p>
                <Button size="sm" className="mt-auto" onClick={() => onOpen(selected.id)}>
                  Open todo
                </Button>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-muted-foreground">
                Select a todo to preview
              </div>
            )}
          </aside>
        )}
      </div>
      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          onClose={() => setCtx(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                setSelectedId(ctx.id)
                setPreviewOpen(true)
              },
            },
            {
              label: 'Open',
              icon: ExternalLink,
              onSelect: () => onOpen(ctx.id),
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function ActivityPanel({
  events,
  projects,
  kind,
  linkedOnly,
  who,
  projectId,
  listeningHealth,
  onKind,
  onLinkedOnly,
  onWho,
  onProjectId,
  onOpen,
  onListening,
  onCycleListeningHealth,
}: {
  events: ActivityEvent[]
  projects: { id: string; name: string; listening: boolean }[]
  kind: 'all' | ActivityEvent['kind']
  linkedOnly: boolean
  who: 'org' | 'team' | 'mine'
  projectId: 'all' | string
  listeningHealth: 'connected' | 'degraded' | 'disconnected'
  onKind: (k: 'all' | ActivityEvent['kind']) => void
  onLinkedOnly: (v: boolean) => void
  onWho: (w: 'org' | 'team' | 'mine') => void
  onProjectId: (id: 'all' | string) => void
  onOpen: (id: string) => void
  onListening: () => void
  onCycleListeningHealth: () => void
}) {
  const kindFilters: { id: 'all' | ActivityEvent['kind']; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'comment', label: 'Comments' },
    { id: 'completed', label: 'Completions' },
    { id: 'updated', label: 'Updates' },
  ]

  const whoFilters: { id: 'org' | 'team' | 'mine'; label: string }[] = [
    { id: 'org', label: 'Org' },
    { id: 'team', label: 'Team' },
    { id: 'mine', label: 'Mine' },
  ]

  const listeningProjects = projects.filter((p) => p.listening)

  const healthLabel =
    listeningHealth === 'connected'
      ? 'Listening connected'
      : listeningHealth === 'degraded'
        ? 'Listening issues'
        : 'Listening off'

  const healthIcon =
    listeningHealth === 'connected' ? (
      <Radio className="h-4 w-4 text-emerald-600" />
    ) : listeningHealth === 'degraded' ? (
      <AlertTriangle className="h-4 w-4 text-amber-600" />
    ) : (
      <Radio className="h-4 w-4 text-muted-foreground opacity-50" />
    )

  const dayGroups = useMemo(() => {
    const order: string[] = []
    const map = new Map<string, ActivityEvent[]>()
    for (const e of events) {
      if (!map.has(e.dayKey)) {
        map.set(e.dayKey, [])
        order.push(e.dayKey)
      }
      map.get(e.dayKey)!.push(e)
    }
    return order.map((key) => ({
      key,
      label: map.get(key)![0].dayLabel,
      events: map.get(key)!,
      people: new Set(map.get(key)!.map((e) => e.actor)).size,
    }))
  }, [events])

  const [selectedEventId, setSelectedEventId] = useState<string | null>(
    events[0]?.id ?? null,
  )
  const [previewOpen, setPreviewOpen] = useState(false)
  const [ctx, setCtx] = useState<{ x: number; y: number; id: string } | null>(null)
  const selectedEvent = events.find((e) => e.id === selectedEventId) ?? null

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={testIds.basecampExtensionTest.activityPanel}
    >
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Activity</h1>
            <p className="text-xs text-muted-foreground">
              Event log across Listening projects · not Inbox (Rule 8). Vision mock.
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <PreviewPanelToggle
              open={previewOpen}
              onToggle={() => {
                const next = !previewOpen
                setPreviewOpen(next)
                if (next && !selectedEventId && events[0]) {
                  setSelectedEventId(events[0].id)
                }
              }}
              testId={testIds.basecampExtensionTest.activityPreviewToggle}
            />
            <button
              type="button"
              title={`${healthLabel} - open Listening settings. Shift+click to cycle status.`}
              aria-label={`${healthLabel}. Open Listening settings.`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background hover:bg-accent"
              onClick={(e) => {
                if (e.shiftKey) {
                  onCycleListeningHealth()
                  return
                }
                onListening()
              }}
            >
              {healthIcon}
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {whoFilters.map((f) => (
            <Button
              key={f.id}
              size="sm"
              variant={who === f.id ? 'secondary' : 'ghost'}
              className="h-7 text-xs"
              onClick={() => onWho(f.id)}
            >
              {f.label}
            </Button>
          ))}
          <select
            className="h-7 rounded-md border border-input bg-background px-2 text-xs"
            value={projectId}
            onChange={(e) => onProjectId(e.target.value)}
            aria-label="Filter by project"
          >
            <option value="all">All projects</option>
            {listeningProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {kindFilters.map((f) => (
            <Button
              key={f.id}
              size="sm"
              variant={kind === f.id ? 'secondary' : 'ghost'}
              className="h-7 text-xs"
              onClick={() => onKind(f.id)}
            >
              {f.label}
            </Button>
          ))}
          <label className="ml-1 flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={linkedOnly}
              onChange={(e) => onLinkedOnly(e.target.checked)}
            />
            Linked only
          </label>
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        {events.length === 0 ? (
          <div className="flex flex-1 flex-col justify-center px-10 py-12">
            <h2 className="text-lg font-semibold">No events in this filter</h2>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Listening is off for some projects - enable them in Settings to fill this
              feed. Or widen Org / Team / Mine and project filters.
            </p>
            <Button className="mt-6 w-fit" variant="secondary" onClick={onListening}>
              Open Listening
            </Button>
          </div>
        ) : (
          <div className="min-w-0 flex-1 overflow-auto">
            {dayGroups.map((group) => (
              <section key={group.key}>
                <div className="flex items-center justify-between gap-3 border-y border-border/70 bg-muted/40 px-6 py-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    {group.label}
                  </h2>
                  <span className="text-[11px] text-muted-foreground">
                    {group.people} {group.people === 1 ? 'person' : 'people'} active
                  </span>
                </div>
                <ul className="relative">
                  {group.events.map((e, i) => (
                    <li key={e.id} className="relative">
                      {i < group.events.length - 1 ? (
                        <span
                          className="absolute left-[4.85rem] top-8 bottom-0 w-px bg-border"
                          aria-hidden
                        />
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setSelectedEventId(e.id)}
                        onDoubleClick={() => onOpen(e.todoId)}
                        onContextMenu={(ev) => {
                          ev.preventDefault()
                          setSelectedEventId(e.id)
                          setCtx({ x: ev.clientX, y: ev.clientY, id: e.id })
                        }}
                        className={cn(
                          'flex w-full items-start gap-3 px-6 py-3.5 text-left hover:bg-accent/40',
                          selectedEventId === e.id && 'bg-accent/40',
                        )}
                      >
                        <div className="w-14 shrink-0 pt-1 text-right text-[11px] tabular-nums text-muted-foreground">
                          {e.time}
                        </div>
                        <div className="relative z-[1] mt-1.5 flex h-3 w-3 shrink-0 items-center justify-center">
                          <span
                            className={cn(
                              'h-2.5 w-2.5 rounded-full ring-2 ring-background',
                              kindDotClass(e.kind),
                            )}
                          />
                        </div>
                        <div
                          className={cn(
                            'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white',
                            avatarTone(e.actor),
                          )}
                          aria-hidden
                        >
                          {actorInitials(e.actor)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-snug">
                            <span className="font-semibold">{e.actor}</span>{' '}
                            <span className="text-muted-foreground">
                              {activityVerb(e.kind)}
                            </span>{' '}
                            <span className="font-medium text-foreground underline-offset-2 hover:underline">
                              {e.title}
                            </span>{' '}
                            <span className="text-muted-foreground">
                              - {e.project}
                            </span>
                          </p>
                          {e.excerpt ? (
                            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                              {e.excerpt}
                            </p>
                          ) : null}
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <Badge variant="outline" className="h-5 font-normal text-[10px]">
                              {kindLabel(e.kind)}
                            </Badge>
                            {e.linked ? (
                              <Badge
                                variant="secondary"
                                className="h-5 font-normal text-[10px]"
                              >
                                linked
                              </Badge>
                            ) : null}
                            {e.flowStatus ? (
                              <span className="text-[10px] text-muted-foreground">
                                {e.flowStatus}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
        {previewOpen && (
          <aside className="flex w-80 shrink-0 flex-col border-l border-border">
            {selectedEvent ? (
              <div className="flex flex-1 flex-col overflow-auto p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </p>
                <h2 className="mt-2 text-sm font-semibold leading-snug">
                  {selectedEvent.title}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {selectedEvent.actor} · {selectedEvent.dayLabel} · {selectedEvent.time}
                </p>
                <p className="mt-3 text-xs text-muted-foreground">
                  {selectedEvent.project} · {kindLabel(selectedEvent.kind)}
                </p>
                {selectedEvent.excerpt ? (
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {selectedEvent.excerpt}
                  </p>
                ) : null}
                <Button
                  size="sm"
                  className="mt-auto"
                  onClick={() => onOpen(selectedEvent.todoId)}
                >
                  Open todo
                </Button>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-muted-foreground">
                Select an event to preview
              </div>
            )}
          </aside>
        )}
      </div>
      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          onClose={() => setCtx(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                setSelectedEventId(ctx.id)
                setPreviewOpen(true)
              },
            },
            {
              label: 'Open todo',
              icon: ExternalLink,
              onSelect: () => {
                const ev = events.find((e) => e.id === ctx.id)
                if (ev) onOpen(ev.todoId)
              },
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function TreeRow({
  depth,
  label,
  icon,
  expanded,
  onToggle,
  onActivate,
  active,
  badge,
  testId,
}: {
  depth: number
  label: string
  icon: React.ReactNode
  expanded?: boolean
  onToggle?: () => void
  onActivate?: () => void
  active?: boolean
  badge?: React.ReactNode
  testId?: string
}) {
  const expandable = Boolean(onToggle)
  return (
    <SidebarTreeRow
      padLeft={PAD[Math.min(depth, PAD.length - 1)]}
      active={active}
      icon={icon}
      label={label}
      badge={badge}
      data-testid={testId}
      aria-level={depth + 1}
      aria-expanded={expandable ? expanded : undefined}
      chevron={
        expandable
          ? {
              expanded: Boolean(expanded),
              onClick: (e) => {
                e.stopPropagation()
                onToggle?.()
              },
            }
          : undefined
      }
      onClick={() => {
        if (onActivate) onActivate()
        else if (onToggle) onToggle()
      }}
      className="cursor-pointer"
    />
  )
}

function Field({
  label,
  value,
  hint,
  readOnly,
}: {
  label: string
  value: string
  hint?: string
  readOnly?: boolean
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Input value={value} readOnly={readOnly} />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function TodoViewer({
  todo,
  forceEmptyNotes,
  pulledAt,
  onOpenCreate,
  onPull,
  onUnlink,
  onToggleBookmark,
  onNote,
}: {
  todo: BcTodo
  forceEmptyNotes?: boolean
  pulledAt: string | null
  onOpenCreate: () => void
  onPull: () => void
  onUnlink: () => void
  onToggleBookmark: () => void
  onNote: (msg: string) => void
}) {
  type Promoted = 'comments' | 'work-specs' | 'related' | 'actions' | null
  const [promoted, setPromoted] = useState<Promoted>(null)
  const [selectedSpecId, setSelectedSpecId] = useState<string | null>(null)

  const linked = Boolean(todo.linkedDoc)
  const notes = forceEmptyNotes ? '' : todo.notes
  const hasNotes = notes.trim().length > 0
  const panelOpen = promoted !== null

  const workSpecs = useMemo(() => {
    if (todo.linkedDoc) {
      return [
        {
          id: 'ws1',
          title: todo.linkedDoc.title,
          status: todo.workSpec === 'stale' ? 'stale' : 'active',
          project: todo.project,
          primary: true,
          excerpt:
            'Launched prod phases A/B. Remaining: Lauren recategorization deferred.',
        },
        {
          id: 'ws2',
          title: 'Events page IA notes',
          status: 'draft',
          project: todo.project,
          primary: false,
          excerpt: 'Early sketch of Past / Upcoming / Featured.',
        },
      ]
    }
    return [
      {
        id: 'ws-suggest',
        title: 'Suggested: create work spec from this todo',
        status: 'none',
        project: todo.project,
        primary: false,
        excerpt: 'No linked FlowNotes doc yet.',
      },
    ]
  }, [todo])

  const relatedItems = useMemo(
    () => [
      {
        id: 'r1',
        title: 'Implement new banner and label treatment',
        status: 'active',
        project: 'Greater Good Science Center',
      },
      {
        id: 'r2',
        title: 'Homepage hero copy refresh',
        status: 'completed',
        project: 'Greater Good Science Center',
      },
      {
        id: 'r3',
        title: 'Quiz publishing proposal',
        status: 'draft',
        project: 'GGIA',
      },
    ],
    [],
  )

  const actions = [
    {
      id: 'a1',
      label: 'View in Basecamp',
      hint: 'Open this todo in Basecamp',
      icon: ExternalLink,
    },
    {
      id: 'a2',
      label: 'Assess work spec',
      hint: 'Run AI triage in chat (staleness, blockers, next step)',
      icon: Search,
    },
    {
      id: 'a3',
      label: 'Update Work Spec',
      hint: 'Update or create work spec for this todo',
      icon: RefreshCw,
    },
    {
      id: 'a4',
      label: 'Draft a message',
      hint: 'Create a draft message or comment',
      icon: MessageSquare,
    },
    {
      id: 'a5',
      label: 'Find more context',
      hint: 'Search related documents',
      icon: Search,
    },
  ]

  function promote(section: Exclude<Promoted, null>) {
    setPromoted(section)
    if (section !== 'work-specs') setSelectedSpecId(null)
  }

  function demote() {
    setPromoted(null)
    setSelectedSpecId(null)
  }

  function togglePanel() {
    if (panelOpen) demote()
    else promote('work-specs')
  }

  const selectedSpec =
    workSpecs.find((s) => s.id === selectedSpecId) ?? workSpecs[0] ?? null

  return (
    <div
      key={todo.id + (linked ? '-linked' : '') + (hasNotes ? '' : '-empty')}
      className="flex h-full min-h-0 flex-col animate-in fade-in slide-in-from-bottom-1 duration-300 ease-out"
      data-testid={
        hasNotes
          ? testIds.basecampExtensionTest.todoViewer
          : testIds.basecampExtensionTest.todoEmptyNotes
      }
    >
      {/* Toolbar - middle path: BC crumbs + FN panel/bookmark controls */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-2">
        <div className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {todo.project} <span className="opacity-40">/</span> To-Dos{' '}
          <span className="opacity-40">/</span> {todo.list}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 gap-1"
          data-testid={testIds.basecampExtensionTest.todoBookmarkToggle}
          onClick={onToggleBookmark}
          title={todo.bookmarked ? 'Remove bookmark' : 'Bookmark'}
        >
          <Bookmark
            className={cn(
              'h-3.5 w-3.5',
              todo.bookmarked && 'fill-amber-500 text-amber-600',
            )}
          />
          {todo.bookmarked ? 'Bookmarked' : 'Bookmark'}
        </Button>
        <Button
          size="icon"
          variant={panelOpen ? 'secondary' : 'ghost'}
          className="h-7 w-7"
          data-testid={testIds.basecampExtensionTest.todoPanelToggle}
          title={panelOpen ? 'Hide secondary panel' : 'Show secondary panel'}
          onClick={togglePanel}
        >
          <PanelRight className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" title="More">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </div>

      {linked && (
        <div
          className="flex items-center gap-2 border-b border-border px-4 py-2 text-xs"
          data-testid={testIds.basecampExtensionTest.docBadge}
        >
          <Badge variant="secondary" className="gap-1 font-normal">
            <Tent className="h-3 w-3" />
            Basecamp
          </Badge>
          <span className="truncate font-medium">{todo.name}</span>
          <span className="text-muted-foreground">· {formatDue(todo.dueOn)}</span>
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" className="h-7" onClick={onPull}>
              <RefreshCw className="mr-1 h-3 w-3" />
              Pull
            </Button>
            <Button size="sm" variant="ghost" className="h-7" asChild>
              <a
                href={`https://app.basecamp.com/5744549/buckets/39953224/todos/${todo.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="mr-1 h-3 w-3" />
                Basecamp
              </a>
            </Button>
          </div>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-auto @container">
          <div
            className={cn(
              'grid gap-6 px-6 py-6',
              panelOpen
                ? 'grid-cols-1'
                : 'grid-cols-1 @[860px]:grid-cols-[minmax(0,1fr)_17rem] @[860px]:gap-6',
            )}
          >
            {/* Details - Basecamp-inspired */}
            <div className="min-w-0 max-w-3xl space-y-6">
              <div>
                <div className="flex items-start gap-2">
                  {todo.completed ? (
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-emerald-600" />
                  ) : null}
                  <h1 className="text-[1.55rem] font-semibold leading-tight tracking-tight">
                    {todo.name}
                  </h1>
                </div>
                <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                  <input type="checkbox" checked={todo.completed} readOnly />
                  Mark as complete
                </label>
              </div>

              <div className="space-y-2.5 text-sm">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="w-24 shrink-0 text-muted-foreground">
                    Assigned to
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white',
                        avatarTone(todo.assignee ?? 'U'),
                      )}
                    >
                      {actorInitials(todo.assignee ?? 'U')}
                    </span>
                    <span className="font-medium">
                      {todo.assignee ?? 'Unassigned'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="w-24 shrink-0 text-muted-foreground">Due on</span>
                  <span
                    className={cn(
                      'font-medium',
                      todo.daysOverdue ? 'text-red-600' : 'text-foreground',
                    )}
                  >
                    {formatDue(todo.dueOn)}
                    {todo.daysOverdue ? ` (${todo.daysOverdue}d overdue)` : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="w-24 shrink-0 text-muted-foreground">
                    Comments
                  </span>
                  <button
                    type="button"
                    className="font-medium underline-offset-2 hover:underline"
                    onClick={() => promote('comments')}
                  >
                    {todo.comments.length}
                  </button>
                </div>
              </div>

              <section>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Notes
                  </h2>
                  {!linked && (
                    <Button size="sm" variant="link" className="h-auto p-0 text-xs" asChild>
                      <a
                        href={`https://app.basecamp.com/5744549/buckets/39953224/todos/${todo.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in Basecamp
                      </a>
                    </Button>
                  )}
                </div>
                {hasNotes ? (
                  <div className="max-w-prose space-y-3 text-sm leading-relaxed">
                    {notes.split(/\n\n+/).map((block, i) => {
                      const lines = block.split('\n')
                      const listLines = lines.filter((l) => /^[-*]\s+/.test(l.trim()))
                      if (listLines.length >= 2 && listLines.length === lines.length) {
                        return (
                          <ul key={i} className="list-disc space-y-1 pl-5">
                            {listLines.map((l, j) => (
                              <li key={j}>{l.replace(/^[-*]\s+/, '')}</li>
                            ))}
                          </ul>
                        )
                      }
                      return (
                        <p key={i} className="whitespace-pre-wrap">
                          {block}
                        </p>
                      )
                    })}
                    <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">ggsc-events-redesign.html</div>
                        <div className="text-muted-foreground">28.3 KB · attachment mock</div>
                      </div>
                      <span className="text-muted-foreground">Download</span>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-md space-y-3 border-t border-border pt-4">
                    <p className="text-sm font-medium">No notes on this todo</p>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Basecamp description is empty (or not pulled yet). Open Basecamp
                      to add notes, or Pull after someone updates them.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={onPull}>
                        <RefreshCw className="mr-1 h-3.5 w-3.5" />
                        Pull from Basecamp
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* Widgets - existing FN promote pattern */}
            <div className="space-y-3">
              <MinimizedSection
                type="comments"
                title="Comments"
                count={todo.comments.length}
                isPromoted={promoted === 'comments'}
                onPromote={() => promote('comments')}
                onDemote={demote}
                preview={
                  todo.comments[0] ? (
                    <div className="flex items-start gap-2 text-xs">
                      <div
                        className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white',
                          avatarTone(todo.comments[0].author),
                        )}
                      >
                        {actorInitials(todo.comments[0].author)}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {todo.comments[0].author}
                        </p>
                        <p className="line-clamp-1 text-muted-foreground">
                          {todo.comments[0].text}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No comments yet</p>
                  )
                }
              />
              <MinimizedSection
                type="work-specs"
                title="Work Specs"
                count={workSpecs.length}
                isPromoted={promoted === 'work-specs'}
                onPromote={() => promote('work-specs')}
                onDemote={demote}
                preview={
                  <div className="space-y-1">
                    <p className="truncate text-xs text-muted-foreground">
                      {workSpecs[0]?.title}
                    </p>
                    <div className="flex items-center gap-1">
                      {workSpecs[0]?.status !== 'none' ? (
                        <span className="rounded bg-muted px-1 text-[10px]">
                          {workSpecs[0]?.status}
                        </span>
                      ) : null}
                      <span className="text-xs text-muted-foreground">
                        {workSpecs[0]?.project}
                      </span>
                    </div>
                  </div>
                }
              />
              <MinimizedSection
                type="related"
                title="Related"
                count={relatedItems.length}
                isPromoted={promoted === 'related'}
                onPromote={() => promote('related')}
                onDemote={demote}
                preview={
                  <div className="space-y-1">
                    {relatedItems.slice(0, 2).map((r) => (
                      <p key={r.id} className="truncate text-xs text-muted-foreground">
                        {r.title}
                      </p>
                    ))}
                    {relatedItems.length > 2 ? (
                      <p className="text-xs text-muted-foreground opacity-60">
                        +{relatedItems.length - 2} more
                      </p>
                    ) : null}
                  </div>
                }
              />
              <MinimizedSection
                type="actions"
                title="Actions"
                count={actions.length}
                isPromoted={promoted === 'actions'}
                onPromote={() => promote('actions')}
                onDemote={demote}
                preview={
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Update Work Spec</p>
                    <p className="text-xs text-muted-foreground opacity-60">
                      + {actions.length - 1} more actions
                    </p>
                  </div>
                }
              />
              {!linked ? (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={onOpenCreate}
                  data-testid={testIds.basecampExtensionTest.createSpecOpen}
                >
                  Create work spec
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Secondary panel */}
        {panelOpen && (
          <aside
            className="flex w-[22rem] shrink-0 flex-col border-l border-border bg-background"
            data-testid={testIds.basecampExtensionTest.todoSecondaryPanel}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {promoted === 'comments'
                  ? 'Comments'
                  : promoted === 'work-specs'
                    ? 'Work Specs'
                    : promoted === 'related'
                      ? 'Related'
                      : 'Actions'}
              </h2>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={demote}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-3">
              {promoted === 'comments' && (
                <ul className="space-y-4">
                  {todo.comments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No comments yet</p>
                  ) : (
                    todo.comments.map((c) => (
                      <li key={c.id} className="flex gap-2.5">
                        <div
                          className={cn(
                            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white',
                            avatarTone(c.author),
                          )}
                        >
                          {actorInitials(c.author)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5 text-xs">
                            <span className="font-semibold">{c.author}</span>
                            {c.role ? (
                              <Badge
                                variant="outline"
                                className="h-4 border-amber-400/50 bg-amber-500/10 px-1 text-[9px] text-amber-800"
                              >
                                {c.role}
                              </Badge>
                            ) : null}
                            <span className="text-muted-foreground">{c.at}</span>
                          </div>
                          <p className="mt-1 text-sm leading-relaxed">{c.text}</p>
                        </div>
                      </li>
                    ))
                  )}
                  <Button size="sm" variant="outline" className="w-full" onClick={onPull}>
                    Pull latest
                  </Button>
                </ul>
              )}

              {promoted === 'work-specs' && (
                <div className="space-y-2">
                  <p className="px-1 pb-1 text-[11px] text-muted-foreground">
                    Click selects · double-click opens · panel is the preview surface
                  </p>
                  {workSpecs.map((spec) => {
                    const selected = selectedSpecId === spec.id || (!selectedSpecId && spec.primary)
                    return (
                      <button
                        key={spec.id}
                        type="button"
                        onClick={() => setSelectedSpecId(spec.id)}
                        onDoubleClick={() => {
                          if (spec.status === 'none') onOpenCreate()
                          else onNote('Would open work spec in tab')
                        }}
                        className={cn(
                          'w-full rounded-lg border p-3 text-left transition-colors',
                          selected
                            ? 'border-primary/40 bg-primary/10'
                            : 'border-border hover:bg-muted/40',
                        )}
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-medium">{spec.title}</span>
                          {spec.primary ? (
                            <Badge className="h-4 px-1 text-[9px]">Primary</Badge>
                          ) : null}
                          {spec.status !== 'none' ? (
                            <Badge variant="secondary" className="h-4 px-1 text-[9px]">
                              {spec.status}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                          {spec.excerpt}
                        </p>
                        <p className="mt-1.5 text-[11px] text-muted-foreground">
                          {spec.project}
                          {pulledAt
                            ? ` · pulled ${new Date(pulledAt).toLocaleString()}`
                            : ''}
                        </p>
                      </button>
                    )
                  })}
                  {linked && selectedSpec ? (
                    <div className="mt-3 space-y-2 border-t border-border pt-3">
                      <Button size="sm" variant="outline" className="w-full" onClick={onPull}>
                        Pull into doc
                      </Button>
                      <Button size="sm" variant="ghost" className="w-full" onClick={onUnlink}>
                        Unlink
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      className="mt-2 w-full"
                      onClick={onOpenCreate}
                      data-testid={testIds.basecampExtensionTest.createSpecOpen}
                    >
                      Create work spec
                    </Button>
                  )}
                </div>
              )}

              {promoted === 'related' && (
                <ul className="space-y-2">
                  {relatedItems.map((r) => (
                    <li
                      key={r.id}
                      className="rounded-lg border border-border px-3 py-2.5 hover:bg-muted/40"
                    >
                      <div className="text-sm font-medium">{r.title}</div>
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Badge variant="secondary" className="h-4 px-1 text-[9px]">
                          {r.status}
                        </Badge>
                        {r.project}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {promoted === 'actions' && (
                <ul className="space-y-2">
                  {actions.map((a) => {
                    const Icon = a.icon
                    return (
                      <li key={a.id}>
                        <button
                          type="button"
                          className="flex w-full items-start gap-3 rounded-lg border border-border px-3 py-2.5 text-left hover:bg-muted/40"
                          onClick={() => {
                            if (a.id === 'a3' && !linked) onOpenCreate()
                          }}
                        >
                          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                          <div>
                            <div className="text-sm font-medium">{a.label}</div>
                            <div className="text-xs text-muted-foreground">{a.hint}</div>
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  )
}

